import React, { useState } from 'react';
import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    Paper,
    Alert,
    CircularProgress,

} from '@mui/material';
import { Link } from 'react-router-dom';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';

const ForgotPasswordPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const validateEmail = (email: string) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) return 'Email is required';
        if (!re.test(email)) return 'Enter a valid email';
        return '';
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        setEmailError('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate email
        const error = validateEmail(email);
        if (error) {
            setEmailError(error);
            return;
        }

        setIsSubmitting(true);
        setSubmitError('');

        try {
            const auth = getAuth();

            await sendPasswordResetEmail(auth, email,
                {
                    url: `${window.location.protocol}//${window.location.host}/reset-password`,
                    handleCodeInApp: true,
                }
            );

            // Simulating successful request
            setSubmitSuccess(true);

            // Reset form
            setEmail('');
        } catch (error) {
            setSubmitError('Failed to submit request. Please try again.');
            console.error('Password reset request failed:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className='backdrop'>
            <Box sx={{ maxWidth: 400, mx: 'auto' }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <img src="/logo.png" alt="Logo" style={{ height: "40px" }} />
                    </Box>
                    <Typography variant="h5" component="h1" alignItems="center" gutterBottom>
                        Forgot Password
                    </Typography>

                    <Typography variant="body1" align="center" sx={{ mb: 3 }}></Typography>

                    {submitSuccess ? (
                        <Alert severity="success" sx={{ mb: 3 }}>
                            Password reset instructions have been sent to your email address.
                        </Alert>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            {submitError && (
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    {submitError}
                                </Alert>
                            )}

                            <TextField
                                fullWidth
                                id="email"
                                name="email"
                                label="Email Address"
                                variant="outlined"
                                margin="normal"
                                value={email}
                                onChange={handleEmailChange}
                                error={Boolean(emailError)}
                                helperText={emailError}
                                disabled={isSubmitting}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                size="large"
                                disabled={isSubmitting}
                                sx={{ mt: 3, mb: 2 }}
                            >
                                {isSubmitting ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    'Send Reset Instructions'
                                )}
                            </Button>
                        </form>
                    )}

                    <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                        Remember your password? <Link to="/login">Back to login</Link>
                    </Typography>
                </Paper>
            </Box>
        </div>
    );
};

export default ForgotPasswordPage;
